import { useEffect, useState, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithPopup, type User } from 'firebase/auth';
import { useTheme, AlertBanner } from '@flowcore-water/ui';
import { auth, googleProvider } from '../lib/firebase';

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTheme();

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        style={{ background: t.pageBg }}
      >
        <span style={{ color: t.textMuted }}>Loading...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className="flex h-screen flex-col items-center justify-center gap-6"
        style={{ background: t.pageBg }}
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold" style={{ color: t.textPrimary }}>
            ST Mirror Browser
          </h1>
          <p className="mt-2 text-sm" style={{ color: t.textMuted }}>
            Sign in with your Flowcore Water account
          </p>
        </div>
        {error && (
          <AlertBanner
            variant="error"
            message={error}
            onRetry={() => {
              setError(null);
              signInWithPopup(auth, googleProvider).catch((e) => setError(e.message));
            }}
          />
        )}
        <button
          onClick={() => {
            setError(null);
            signInWithPopup(auth, googleProvider).catch((e) => setError(e.message));
          }}
          className="rounded-lg border px-6 py-2.5 text-sm font-medium transition-colors"
          style={{
            background: t.accent,
            color: '#fff',
            borderColor: t.accent,
          }}
        >
          Sign in with Google
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
